import utils from '../utils';
import DEFAULTS from '../constants/defaults';
import { SASH_LABEL_ID } from '../constants/labels';

// List of decorators used by Design Pro
const BASIC_DECORATORS = ['SplitNodeDecorator'];

// List of decorators used by Web Segment
const WEB_SEGMENT_DECORATORS = [
  'SplitNodeDecorator',
  'FoundationNodeDecorator',
  'LeftOutsideCornerTrimNodeDecorator',
  'RightOutsideCornerTrimNodeDecorator',
  'TopTrimNodeDecorator',
  'BottomTrimNodeDecorator',
];

/**
 * MacheteController abstracts out machete.
 * This contains methods to manipulate the model, viewer and segmentation tree.
 * @example
 * const macheteController = new MacheteController({
 *   outlineColor,
 *   container,
 *   viewerOpts,
 * });
 */
export default class MacheteController {
  viewer = null;
  model = null;
  outlineColor = '#000000';

  /**
   * @param {Object} options
   * @param {string} options.outlineColor - color for outlining elements
   * @param {react component} options.container - parent container to render machete in
   * @param {Object} options.viewerOpts - options for machete viewer
   */
  constructor({ outlineColor, container, viewerOpts } = {}) {
    this.outlineColor = outlineColor;
    this.viewer = new Hover.Machete.Viewer(container, viewerOpts);
    const layer = this.viewer.layers.get('helperLayer');
    // TODO: aarosil - rm this when pointer events supported in machete-redux.
    this.measureTool = new Hover.Machete.MeasureTool(this.viewer, layer);

    let originalCursor;
    this.measureTool.addEventListener('enabled', () => {
      originalCursor = document.body.style.cursor;
      document.body.style.cursor = 'crosshair';
    });

    this.measureTool.addEventListener('disabled', () => {
      document.body.style.cursor = originalCursor;
    });
  }

  /**
   * loadModel renders the machete model.
   * @param {Object} options
   * @param {Object} options.geometry - geometry for machete to render
   * @param {Object} options.metadata - metadata used by machete to render
   * @param {boolean} options.autotexture - option for machete to handle texturing
   * @param {boolean} options.autofocus - option to reset focus after model load
   * @example
   * this.loadModel(options)
   */
  loadModel(options) {
    const model = new Hover.Building();
    this.model = model;

    return new Promise((resolve) => {
      const modelOpts = {
        ...options,
        onComplete: () => resolve(model),
      };

      model.load(modelOpts);
    });
  }

  createModels() {
    const { original, saved } = this.models;
    original.model = new Hover.Building();

    const showModel = (model) => {
      this.selectAndShowModel(model);
      this.focusModel();
    };

    if (saved) {
      saved.model = new Hover.Building();
      saved.opts.onComplete = () => {
        this.textureModel(saved.model);
        showModel(saved.model);
      };

      original.opts.onComplete = () => {
        this.textureModel(original.model);
      };
    } else {
      original.opts.onComplete = () => {
        this.textureModel(original.model);
        showModel(original.model);
      };
    }
  }

  loadTexturesToLibrary(textures) {
    this.library = Hover.TexturingLibrary.getInstance();
    const texturesUrl = '/hover_models/products.json';
    const { original, saved } = this.models;

    if (Array.isArray(textures)) {
      this.library.setLibraryObject('textures', textures);
      if (saved) saved.model.load(saved.opts);
      original.model.load(original.opts);
    } else {
      this.library.fetchTextures(texturesUrl).then(() => {
        if (saved) saved.model.load(saved.opts);
        original.model.load(original.opts);
      });
    }
  }

  initModels(modelOpts, textures) {
    this.models = { ...modelOpts };

    this.createModels();
    this.loadTexturesToLibrary(textures);
  }

  resetModel(id) {
    const modelData =
      id === 'original' ? this.models.original : this.models.saved;
    const newModel = new Hover.Building();

    // we only want to texture the new model after loading
    modelData.opts.onComplete = () => {
      this.textureModel(modelData.model);
    };
    newModel.load(modelData.opts);
    modelData.model = newModel;
  }

  selectAndShowModel(model) {
    this.model = model;

    this.viewer.modelContainer.removeAllModels();
    this.viewer.modelContainer.addModel(this.model);
  }

  // textureModel should only be run when loading a model
  // because manowarData doesn't change when we edit the models
  textureModel(model) {
    model.elements.forEach((modelElement) => {
      const data = modelElement.manowarData;

      this.library.getTextures().then((textureDataList) => {
        const selectedTexture = textureDataList.find(
          (textureDataElement) => textureDataElement.id === data.textureId,
        );

        if (selectedTexture) {
          modelElement.textureFromData(selectedTexture);
        }

        modelElement.color = data.colorHex;
      });
    });
  }

  // TODO: aarosil - rm this when pointer events supported in machete-redux.
  // (see comment in constructor above)
  toggleMeasureTool() {
    const { enabled } = this.measureTool;

    this.measureTool.setModel(this.model);

    if (enabled) {
      this.measureTool.disable();
    } else {
      this.measureTool.enable();
    }
  }

  /**
   * focusModel puts the model in focus
   * @example
   * this.focusModel()
   */
  focusModel() {
    this.viewer.focus(this.model, DEFAULTS.focusOpts());
  }

  /**
   * initSegmentationForOrder
   * @param {*} order
   * @param {*} opts
   */
  initSegmentationForOrder(
    order,
    opts = {
      useDebug: false,
      includeOutlines: true,
    },
  ) {
    const { WebSegmentation } = Hover.Machete.Tools;
    const { model } = this;

    this.order = order;

    WebSegmentation.prepareOrder(this.order);

    // For Manowar orders, only include unlabeled facades/annotations
    const elements = model.elements.filter((e) => e.labels.length === 0);
    this.initSegmentation(opts, WEB_SEGMENT_DECORATORS, elements);

    const tree = this.model.segmentationTree;

    const originalAutoupdateNeighborsValue = tree.autoUpdateNeighbors;

    // prevent neighbors from updating automatically
    // it is very expensive and it isn't necessary for most nodes
    // except for :
    // - LeftOutsideCornerTrimNodeDecorator
    // - RightOutsideCornerTrimNodeDecorator
    tree.autoUpdateNeighbors = false;

    // If the model does not contain Sash (Windows) already,
    // then project Markups onto the model
    const hasWindows = model.elements.some((e) =>
      e.labels.includes(SASH_LABEL_ID),
    );
    if (!hasWindows) {
      const originalAutoupdateValue = tree.autoUpdate;

      // prevent a lot of intermediate geometry updates
      tree.autoUpdate = false;

      // add window related nodes to the tree
      WebSegmentation.addOpeningsToSegmentationTree(model.segmentationTree);

      // trigger a manual geometry update for nodes that need update
      tree.update();

      // restore original autoUpdate value
      tree.autoUpdate = originalAutoupdateValue;
    }

    tree.updateNeighbors(tree.nodes);
    tree.autoUpdateNeighbors = originalAutoupdateNeighborsValue;
  }
  // TO DO: fill in descriptions
  /**
   * initSegmentation creates the segmentation tree which has all of the nodes.
   * @param {Object} opts
   * @param {Object} decorators - defines which node types are in the tree
   * @param {Hover.ModelElement[]} elements - list of ModelElements to include
   * in the tree.
   * @example
   * this.initSegmentation(
   *    opts,
   *    decorators,
   * );
   */
  initSegmentation(
    opts = {},
    decorators = BASIC_DECORATORS,
    elements = this.model.elements,
  ) {
    const { WebSegmentation } = Hover.Machete.Tools;
    WebSegmentation.configure(opts);
    WebSegmentation.prepareModel(this.model);
    const tree = this.model.segmentationTree;

    tree.configure({
      defaultDecorators: decorators,
    });

    const originalAutoupdateValue = tree.autoUpdate;
    const originalAutoupdateNeighborsValue = tree.autoUpdateNeighbors;

    // prevent neighbors from updating automatically
    // it is very expensive and it isn't necessary for most nodes
    // except for :
    // - LeftOutsideCornerTrimNodeDecorator
    // - RightOutsideCornerTrimNodeDecorator
    tree.autoUpdateNeighbors = false;

    // prevent a lot of intermediate geometry updates
    tree.autoUpdate = false;

    // compute facades nodes for a given list of model elements
    const { SegmentationUtils } = window.Hover.Machete;
    const { createNodesFromModel } = SegmentationUtils;
    const allNodes = createNodesFromModel(this.model);

    // add "wall" nodes to the tree
    tree.addNodes(allNodes);

    // trigger a manual geometry update for nodes that need update
    tree.start();

    // restore original autoUpdate value
    tree.autoUpdate = originalAutoupdateValue;

    // restore original autoUpdateNeighbors value
    tree.autoUpdateNeighbors = originalAutoupdateNeighborsValue;
  }

  // sets
  /**
   * setOutlineColor sets the color of outlines for elements but doen't actually outline.
   * @param {string} color  - the color for outlines (needs to be a hex color)
   * @example
   * this.setOutlineColor('#000000');
   */
  setOutlineColor(color = '#000000') {
    this.outlineColor = color;
    this.model.elements
      .filter((e) => e instanceof Hover.Facade)
      .forEach((e) => e.outline.configure({ color }));
    this.updateRender();
  }
  /**
   * @private
   * setOpacityForNode set the transparency of the node.
   * @param {Object} node - the segmentation node
   * @param {number} opacity  -  number between 0 and 1
   * @example
   * this.setOpacityForNode(node, 0.25);
   */
  setOpacityForNode(node, opacity) {
    const elements = node.elements || node.modelElements;
    this.setOpacityForElements(elements, opacity);
  }
  /**
   * @private
   * setOpacityForElements set the transparency of the list of elements.
   * @param {Object[]} elements -  these are an array of elements
   * @param {number} opacity  -  number between 0 and 1
   * @example
   * this.setOpacityForElements(elements, 0.25);
   */
  setOpacityForElements(elements, opacity) {
    elements.forEach((e) => {
      const { individualEntity: ent } = e;
      const entity = ent;
      if (e.outline) e.outline.configure({ color: this.outlineColor });
      if (entity) entity.appearance.material.uniforms.opacity.value = opacity;
    });
  }

  // rendering
  resize() {
    if (this.viewer) this.viewer.resize();
  }

  repaintNodeOpacity(nodeRef, opacity) {
    const node = utils.getNode(nodeRef);
    const parent = utils.getParentForElement(node);
    this.setOpacityForNode(parent, opacity);
    // force Machete to re-draw screen
    this.updateRender();
  }

  updateRender() {
    this.viewer.scene.needsUpdate = true;
  }
}
